import React, { useState, useEffect, useRef } from 'react';

const RAZORPAY_KEY = "rzp_test_SZjJJg6jTF5ech";
const RAZORPAY_API = "https://api.samasong.com/sama-music";

const PaymentModal = ({ isOpen, onClose, planDetails, onPaymentComplete }) => {
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const paymentResolved = useRef(false);

  const createOrder = async (planDetails) => {
    try {
   
      const token = localStorage.getItem('user');

      const response = await fetch(`${RAZORPAY_API}/subscribes/create-order`, {
        method: 'POST',
         headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          subscriptionId: planDetails.subscriptionId || "",
          currency: 'INR',
          isAutoRenew: planDetails.isAutoRenew || false,
          
        })
      });
      const data = await response.json();
      console.log('Order response:', response.status, data);
      if (!response.ok) throw new Error(data.error?.description || data.error?.reason || 'Failed to create order');
      return data;
    } catch (err) {
      console.error('Create order error:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (isOpen) {
      setPaymentStatus('idle');
      setErrorMessage('');
      setTransactionId('');
      paymentResolved.current = false;
    }
  }, [isOpen]);

  const handlePayment = async () => {
    if (!window.Razorpay) {
      setErrorMessage('Payment gateway is loading. Please try again.');
      return;
    }

    setPaymentStatus('processing');
    setErrorMessage('');

    try {
      const orderResponse = await createOrder(planDetails);
      const order = orderResponse.data?.order;
      const internalTransactionId = orderResponse.data?.transactionId;

      if (!order?.id) {
        throw new Error('Invalid order ID from API');
      }

      const options = {
        key: orderResponse.data?.keyId || RAZORPAY_KEY,
        order_id: order.id,
        amount: order.amount,
        currency: order.currency,
        name: "Sama Music",
        description: planDetails.planName || "Premium Subscription",
        image: "https://i.pravatar.cc/150",
        theme: {
          color: "#ec4899"
        },
        prefill: {
          contact: localStorage.getItem('userMobile') || '',
          email: ''
        },
        handler: async (response) => {
          paymentResolved.current = true;
          setTransactionId(response.razorpay_payment_id);
          setPaymentStatus('processing');

          try {
            const token = localStorage.getItem('user');
            
            const verifyResponse = await fetch(`${RAZORPAY_API}/subscribes/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: token ? `Bearer ${token}` : ''
              },
              body: JSON.stringify({
                transactionId: internalTransactionId,
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
    
              })
            });
            
            const verifyData = await verifyResponse.json();
            
            if (!verifyResponse.ok) {
              throw new Error(verifyData.error?.description || 'Payment verification failed');
            }

            setPaymentStatus('success');
            onPaymentComplete({
              success: true,
              planDetails,
              transactionId: response.razorpay_payment_id,
              message: 'Payment completed successfully!'
            });
          } catch (err) {
            console.error('Payment verification error:', err);
            setPaymentStatus('failed');
            setErrorMessage(err.message || 'Payment verification failed. Please contact support.');
            onPaymentComplete({
              success: false,
              error: err.message,
              message: 'Payment verification failed. Please contact support.'
            });
          }
        }
      };

      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', (response) => {
        setPaymentStatus('failed');
        setErrorMessage(response.error.description || 'Payment failed. Please try again.');
        onPaymentComplete({
          success: false,
          error: response.error.description,
          message: 'Payment failed. Please try again.'
        });
      });

      rzp.open();

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus('failed');
      const errorMsg = error.message || 'Payment failed. Please try again.';
      setErrorMessage(errorMsg);

      onPaymentComplete({
        success: false,
        error: errorMsg,
        message: 'Payment failed. Please try again.'
      });
    }
  };

  const handleClose = () => {
    if (paymentStatus !== 'processing') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Complete Payment</h2>
          <p className="text-slate-400">Razorpay Payment Gateway</p>
        </div>

        <div className="bg-white/5 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-slate-400">Plan:</span>
            <span className="font-semibold text-white">{planDetails.planName}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-slate-400">Billing:</span>
            <span className="text-white capitalize">{planDetails.billingCycle}</span>
          </div>
          <div className="flex justify-between items-center text-lg font-bold">
            <span className="text-slate-400">Amount:</span>
            <span className="text-fuchsia-400">₹{planDetails.amount}</span>
          </div>
        </div>

        <div className="flex justify-center mb-6">
          <div className="bg-gradient-to-r from-fuchsia-500 to-purple-600 p-4 rounded-full">
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-3 5-3v6zm7 0l-5-3 5-3v6z"/>
            </svg>
          </div>
        </div>

        {paymentStatus === 'idle' && (
          <div className="text-center space-y-4">
            <p className="text-slate-300">
              You will be redirected to Razorpay to complete your payment securely.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white rounded-lg hover:from-fuchsia-700 hover:to-purple-700 transition"
              >
                Pay Now
              </button>
            </div>
          </div>
        )}

        {paymentStatus === 'processing' && (
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fuchsia-500 mx-auto"></div>
            <p className="text-slate-300">Processing payment...</p>
            <p className="text-sm text-slate-400">Please complete payment in the popup</p>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-green-400 mb-2">Payment Successful!</h3>
              <p className="text-slate-300 mb-2">
                Your {planDetails.planName} plan has been activated successfully.
              </p>
              {transactionId && (
                <p className="text-sm text-slate-400">Payment ID: {transactionId}</p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              Continue
            </button>
          </div>
        )}

        {paymentStatus === 'failed' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-red-400 mb-2">Payment Failed</h3>
              <p className="text-slate-300 mb-2">{errorMessage}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
              >
                Close
              </button>
              <button
                onClick={() => setPaymentStatus('idle')}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white rounded-lg hover:from-fuchsia-700 hover:to-purple-700 transition"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-white/10">
          <p className="text-xs text-slate-400 text-center">
            🔒 Your payment is secured by Razorpay
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;